import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format } from 'date-fns';

const AdvisorDashboard = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const [firmName, setFirmName] = useState('Loading...');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [billingSummary, setBillingSummary] = useState(null);
  const [stats, setStats] = useState({
    clients: 0,
    activeMatters: 0,
    completedMatters: 0
  });
  const [recentMatters, setRecentMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [billingActionError, setBillingActionError] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.advisor_firm_id) return;

      try {
        setLoading(true);
        setError(null);

        // 1. Fetch Firm Details
        const { data: firmData } = await supabase
          .from('advisor_firms')
          .select('firm_name, subscription_status')
          .eq('id', profile.advisor_firm_id)
          .single();

        if (firmData) {
          setFirmName(firmData.firm_name);
          setSubscriptionStatus(firmData.subscription_status);
        }

        // 2. Fetch Stats and Billing
        const [clientRes, activeRes, completedRes, billingRes] = await Promise.all([
          supabase.from('clients').select('*', { count: 'exact', head: true }).eq('advisor_firm_id', profile.advisor_firm_id),
          supabase.from('matters').select('*', { count: 'exact', head: true }).eq('advisor_firm_id', profile.advisor_firm_id).neq('status', 'completed'),
          supabase.from('matters').select('*', { count: 'exact', head: true }).eq('advisor_firm_id', profile.advisor_firm_id).eq('status', 'completed'),
          supabase.rpc('current_advisor_billing_summary')
        ]);

        setStats({
          clients: clientRes.count || 0,
          activeMatters: activeRes.count || 0,
          completedMatters: completedRes.count || 0
        });
        if (billingRes.error) {
          console.warn('Billing summary error:', billingRes.error);
        } else {
          const summary = Array.isArray(billingRes.data) ? billingRes.data[0] : billingRes.data;
          setBillingSummary(summary || null);
        }

        // 3. Fetch Recent Matters
        const { data: matters, error: rmErr } = await supabase
          .from('matters')
          .select(`
            id, plan_type, status, created_at,
            client:clients (first_name, last_name, email)
          `)
          .eq('advisor_firm_id', profile.advisor_firm_id)
          .order('created_at', { ascending: false })
          .limit(5);

        if (rmErr) throw rmErr;
        setRecentMatters(matters || []);

      } catch (err) {
        console.error('Dashboard error:', err);
        setError("Error loading dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [profile]);

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(cents / 100);
  };

  const daysUntil = (dateValue) => {
    if (!dateValue) return null;
    const diff = new Date(dateValue).getTime() - Date.now();
    return Math.max(Math.ceil(diff / (1000 * 60 * 60 * 24)), 0);
  };

  const billingAccessLabel = billingSummary?.access_state?.replaceAll('_', ' ') || subscriptionStatus || 'Trialing';
  const trialDaysRemaining = daysUntil(billingSummary?.trial_ends_at);
  const blocksAttorneySubmission = ['trialing', 'restricted', 'canceled', 'incomplete_signup', 'internal_test'].includes(billingSummary?.access_state);
  const canStartCheckout = billingSummary && ['trialing', 'restricted', 'incomplete_signup'].includes(billingSummary.access_state);

  const getFunctionErrorMessage = async (invokeError, fallback) => {
    const response = invokeError?.context;

    if (response?.clone) {
      try {
        const payload = await response.clone().json();
        return payload?.error || payload?.message || invokeError.message || fallback;
      } catch {
        try {
          const text = await response.clone().text();
          return text || invokeError.message || fallback;
        } catch {
          return invokeError.message || fallback;
        }
      }
    }

    return invokeError?.message || fallback;
  };

  const handleStartCheckout = async (billingInterval = 'monthly') => {
    if (checkoutLoading) return;

    try {
      setCheckoutLoading(true);
      setBillingActionError(null);

      const { data, error: checkoutError } = await supabase.functions.invoke('create-billing-checkout', {
        body: {
          billing_interval: billingInterval,
          origin: window.location.origin
        }
      });

      if (checkoutError) {
        throw new Error(await getFunctionErrorMessage(checkoutError, 'Unable to start billing checkout.'));
      }
      if (!data?.url) throw new Error('Stripe checkout did not return a redirect URL.');

      window.location.assign(data.url);
    } catch (err) {
      console.error('Billing checkout error:', err);
      setBillingActionError(err.message || 'Unable to start billing checkout.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const label = status === 'matter_created'
      ? 'plan created'
      : status?.replace('matter', 'plan').replaceAll('_', ' ');
    const colors = status === 'completed' 
      ? 'bg-green-50 text-green-700 border-green-100' 
      : 'bg-blue-50 text-blue-700 border-blue-100';
    
    return (
      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border ${colors}`}>
        {label}
      </span>
    );
  };

  const QuickAction = ({ title, icon, onClick, color = "slate" }) => (
    <button 
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-4 bg-white rounded-xl border border-slate-200 hover:border-${color}-300 hover:shadow-sm transition-all text-center group`}
    >
      <div className={`p-3 bg-${color}-50 rounded-lg group-hover:scale-110 transition-transform mb-3`}>
        <SafeIcon icon={icon} className={`h-6 w-6 text-${color}-600`} />
      </div>
      <span className="text-xs font-bold text-slate-700 uppercase tracking-tighter">{title}</span>
    </button>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 leading-none">Hello, {profile?.first_name}</h1>
          <div className="mt-2 flex items-center space-x-2 text-sm text-slate-500">
            <span className="font-medium">{firmName}</span>
            <span className="text-slate-300">•</span>
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${
              subscriptionStatus === 'trialing' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-blue-50 text-blue-700 border-blue-100'
            }`}>
              {subscriptionStatus || 'Trialing'}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">{error}</div>
      )}

      {billingSummary && (
        <div className={`border rounded-xl p-5 ${
          blocksAttorneySubmission ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'
        }`}>
          {billingActionError && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-3 rounded-md text-sm text-red-700">
              {billingActionError}
            </div>
          )}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiCreditCard} className="h-5 w-5 text-slate-500" />
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-tighter">Account & Usage</h2>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border bg-white text-slate-700 border-slate-200">
                  {billingAccessLabel}
                </span>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border bg-slate-50 text-slate-600 border-slate-200">
                  Stripe {billingSummary.stripe_environment || 'test'}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                {billingSummary.access_state === 'trialing'
                  ? `Trial${trialDaysRemaining !== null ? ` ends in ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'}` : ''}. Attorney submission requires an active subscription.`
                  : `${billingSummary.plan_name || 'Advisor plan'} includes ${billingSummary.included_submissions || 0} attorney submission${billingSummary.included_submissions === 1 ? '' : 's'} per month.`}
              </p>
              {canStartCheckout && (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => handleStartCheckout('monthly')}
                    disabled={checkoutLoading}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-slate-900 text-white text-xs font-bold uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50"
                  >
                    {checkoutLoading ? (
                      <SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />
                    ) : (
                      <SafeIcon icon={FiIcons.FiCreditCard} className="mr-2 h-4 w-4" />
                    )}
                    Activate Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => handleStartCheckout('annual')}
                    disabled={checkoutLoading}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-white text-slate-700 border border-slate-300 text-xs font-bold uppercase tracking-widest hover:bg-slate-50 disabled:opacity-50"
                  >
                    Annual
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate('/billing')}
                className="mt-4 inline-flex items-center text-xs font-bold uppercase tracking-widest text-blue-700 hover:text-blue-900"
              >
                View Billing & Account
                <SafeIcon icon={FiIcons.FiArrowRight} className="ml-2 h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-3 min-w-full lg:min-w-[360px]">
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Used</p>
                <p className="text-xl font-bold text-slate-900">{billingSummary.used_submissions || 0}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Remaining</p>
                <p className="text-xl font-bold text-slate-900">{billingSummary.remaining_submissions || 0}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Price</p>
                <p className="text-xl font-bold text-slate-900">{formatMoney(billingSummary.actual_amount_cents)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { label: 'Total Clients', value: stats.clients, icon: FiIcons.FiUsers, bg: 'bg-slate-50', color: 'text-slate-600', path: '/clients' },
          { label: 'Active Plans', value: stats.activeMatters, icon: FiIcons.FiFolder, bg: 'bg-blue-50', color: 'text-blue-600', path: '/matters' },
          { label: 'Completed', value: stats.completedMatters, icon: FiIcons.FiCheckCircle, bg: 'bg-green-50', color: 'text-green-600' },
        ].map((stat, i) => {
          const CardComponent = stat.path ? 'button' : 'div';

          return (
            <CardComponent
              key={i}
              onClick={stat.path ? () => navigate(stat.path) : undefined}
              className={`bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-center text-left w-full ${
                stat.path ? 'hover:border-slate-300 hover:shadow-md transition-all cursor-pointer' : ''
              }`}
            >
              <div className={`p-3 rounded-lg ${stat.bg} ${stat.color} mr-4`}>
                <SafeIcon icon={stat.icon} className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{stat.label}</p>
                <p className="text-2xl font-bold text-slate-900 leading-none mt-1">{loading ? '...' : stat.value}</p>
              </div>
            </CardComponent>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <QuickAction title="Add Client" icon={FiIcons.FiUserPlus} onClick={() => navigate('/clients/create')} color="slate" />
          <QuickAction title="Create Plan" icon={FiIcons.FiPlusSquare} onClick={() => navigate('/matters/create')} color="blue" />
          <QuickAction title="View Clients" icon={FiIcons.FiUsers} onClick={() => navigate('/clients')} color="slate" />
          <QuickAction title="View Plans" icon={FiIcons.FiFolder} onClick={() => navigate('/matters')} color="slate" />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tighter">Recent Plans</h3>
          <button onClick={() => navigate('/matters')} className="text-xs font-bold text-blue-600 hover:text-blue-800 uppercase tracking-widest">
            View All
          </button>
        </div>
        
        {loading ? (
          <div className="py-12 text-center text-slate-300">
            <SafeIcon icon={FiIcons.FiLoader} className="animate-spin h-8 w-8 mx-auto" />
          </div>
        ) : recentMatters.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Client</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Plan</th>
                  <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-3 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentMatters.map((matter) => (
                  <tr key={matter.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-slate-900">{matter.client?.first_name} {matter.client?.last_name}</div>
                      <div className="text-xs text-slate-500">{matter.client?.email}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-medium">{matter.plan_type}</td>
                    <td className="px-6 py-4"><StatusBadge status={matter.status} /></td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => navigate(`/matters/${matter.id}`)}
                        className="p-1 px-3 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded transition-colors"
                      >
                        VIEW
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-slate-400">
            <p className="text-sm">No plans found.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvisorDashboard;
